import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';
import { addJob, checkJobExists } from '../services/jobService';

const dropdownOptions = {
  educationLevel: [
  'High School',
  'Certificate',
  'Diploma',
  'Degree',
  'Postgraduate Diploma',
  'Masters Degree',
  'PhD/Doctorate',
  'Not Specified',
],
  jobType: ['Full-time', 'Part-time', 'Internship', 'Contract', 'Temporary', 'Attachment', 'Volunteer'],
  experienceLevel: ['Intern', 'Junior', 'Mid', 'Senior', 'Lead', 'Executive'],
  requiredField: [
    'Accounting & Finance',
  'Administrative & Office Support',
  'Agriculture & Environmental Sciences',
  'Architecture & Design',
  'Arts, Culture & Entertainment',
  'Business & Management',
  'Community & Social Services',
  'Construction & Skilled Trades',
  'Customer Service',
  'Data Science & Analytics',
  'Education & Teaching',
  'Engineering & Technical',
  'Healthcare & Medical',
  'Hospitality, Travel & Tourism',
  'Human Resources',
  'Information Technology',
  'Law & Legal Services',
  'Logistics, Transport & Supply Chain',
  'Manufacturing & Production',
  'Marketing, Media & Communications',
  'NGO & Nonprofit',
  'Project & Program Management',
  'Research & Development',
  'Sales & Business Development',
  'Science & Laboratory',
  'Security & Protective Services',
  'Software & Web Development',
  'Sports, Fitness & Recreation',
  'Telecommunications',
  'Training & Coaching',
  'Not Specified'
  ],

  location: [
  'Nairobi',
  'Mombasa',
  'Kisumu',
  'Nakuru',
  'Eldoret',
  'Thika',
  'Malindi',
  'Kitale',
  'Garissa',
  'Kakamega',
  'Machakos',
  'Meru',
  'Nyeri',
  'Kericho',
  'Kisii',
  'Embu',
  'Bungoma',
  'Nanyuki',
  'Isiolo',
  'Marsabit',
  'Lodwar',
  'Wajir',
  'Mandera',
  'Homa Bay',
  'Migori',
  'Voi',
  'Narok',
  'Naivasha',
  'Limuru',
  'Kiambu',
  'Murang’a',
  'Kerugoya',
  'Chuka',
  'Siaya',
  'Busia',
  'Bomet',
  'Kapenguria',
  'Kapsabet',
  'Litein',
  'Sotik',
  'Kilifi',
  'Kwale',
  'Lamu',
  'Taveta',
  'Taita',
  'Moyale',
  'Maralal',
  'Hola',
  'Othaya',
  'Ol Kalou',
  'Kajiado',
  'Loitoktok',
  'Athi River',
  'Kangundo',
  'Mwingi',
  'Wote',
  'Kitui',
  'Kathiani',
  'Makueni',
  'Vihiga (Mbale)',
  'Bondo',
  'Oyugis',
  'Awendo',
  'Kehancha',
  'Other'
],
  organizationCategory: ['Banking', 'Finance', 'Healthcare', 'Education', 'Retail', 'ICT', 'Manufacturing', 'Construction', 'Hospitality', 'Logistics', 'Real Estate', 'Agriculture', 'Media', 'Non-profit', 'Government']
};

const AddJob = () => {
  const navigate = useNavigate();
  const [showPreview, setShowPreview] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const [orgInfo, setOrgInfo] = useState({
    companyName: '',
    companyWebsite: '',
    location: '',
    organizationCategory: '',
    companyDetails: '',
    applicationMethod: '',
    postedDate: new Date().toISOString().split('T')[0],
    closingDate: ''
  });

  const [jobs, setJobs] = useState([
    { title: '', jobType: '', educationLevel: '', requiredField: '', experienceLevel: '', requirements: '', jobUrl: '', imgUrl: '', salaryRange: '', pdfUrl: '' }
  ]);

  const handleOrgChange = (e) => {
    const { name, value } = e.target;
    setOrgInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleJobChange = (index, e) => {
    const { name, value } = e.target;
    const updated = [...jobs];
    updated[index][name] = value;
    setJobs(updated);
  };

  const addJobField = () => {
    setJobs([...jobs, { title: '', jobType: '', educationLevel: '', requiredField: '', experienceLevel: '', requirements: '', jobUrl: '', imgUrl: '', salaryRange: '', pdfUrl: '' }]);
  };

  const removeJobField = (index) => {
    if (jobs.length > 1) {
      const updated = [...jobs];
      updated.splice(index, 1);
      setJobs(updated);
    }
  };

  const handleSubmit = async () => {
    if (!orgInfo.companyName || !orgInfo.closingDate) {
      toast.error('Missing company name or closing date');
      return;
    }

    const combinedJobs = jobs.map((job) => ({ ...orgInfo, ...job }));

    setIsSubmitting(true);
    setSubmitMessage('Submitting jobs...');

    try {
      await addJob(combinedJobs);
      setSubmitMessage(`${combinedJobs.length} job(s) submitted successfully`);
      toast.success(`${combinedJobs.length} job(s) submitted`);
      setTimeout(() => {
        setIsSubmitting(false);
        navigate('/');
      }, 1500);
    } catch (err) {
      console.error(err);
      setSubmitMessage('Submission failed. Please try again.');
      toast.error('Submission failed');
      setTimeout(() => setIsSubmitting(false), 2000);
    }
  };

  const handleCheckJobExists = async () => {
    const promises = jobs.map(job => checkJobExists({
      title: job.title,
      companyName: orgInfo.companyName,
      closingDate: orgInfo.closingDate
    }));

    try {
      const results = await Promise.all(promises);
      const existingJobs = results.map((exists, i) => exists ? jobs[i].title : null).filter(Boolean);

      if (existingJobs.length) {
        toast.warn(`Job(s) already exist: ${existingJobs.join(', ')}`);
      } else {
        toast.success('All jobs are new');
      }
    } catch {
      toast.error('Error checking jobs');
    }
  };

  const renderInput = (name, label, value, onChange, required = false, type = 'text') => (
    <div style={styles.fieldGroup}>
      <label className="form-label">{label}{required && ' *'}</label>
      <input name={name} value={value} onChange={onChange} type={type} className="form-input" />
    </div>
  );

  const renderTextarea = (name, label, value, onChange) => (
    <div style={styles.fieldGroup}>
      <label className="form-label">{label}</label>
      <textarea name={name} value={value} onChange={onChange} rows="3" className="form-input" style={{ resize: 'vertical' }} />
    </div>
  );

  const renderSelect = (name, label, options, index) => {
    const value = index >= 0 ? jobs[index][name] : orgInfo[name];
    const onChange = index >= 0 ? (e) => handleJobChange(index, e) : handleOrgChange;

    return (
      <div style={styles.fieldGroup}>
        <label className="form-label">{label}</label>
        <select name={name} value={value} onChange={onChange} className="form-input">
          <option value="">-- Select --</option>
          {options.map(opt => <option key={opt}>{opt}</option>)}
        </select>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      {/* Progress Dialog */}
      {isSubmitting && (
        <div style={styles.overlay}>
          <div style={styles.dialog}>
            <p>{submitMessage}</p>
            <div style={styles.spinner}></div>
          </div>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}

      <button onClick={() => navigate('/')} className="btn-secondary" style={styles.backButton}>← Back to Dashboard</button>
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>Post Multiple Jobs</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={handleCheckJobExists} className="btn-secondary">👁️ Check if Exists</button>
          <button onClick={() => setShowPreview(true)} className="btn-secondary">👁️ Preview</button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '24px' }}>
        <h3 style={{ marginTop: 0, marginBottom: '16px' }}>Organization Details</h3>
        <div style={styles.rowWrap}>
          {renderInput('companyName', 'Company Name', orgInfo.companyName, handleOrgChange, true)}
          {renderInput('companyWebsite', 'Website', orgInfo.companyWebsite, handleOrgChange)}
        </div>
        <div style={styles.rowWrap}>
          {renderInput('postedDate', 'Posted Date', orgInfo.postedDate, handleOrgChange, false, 'date')}
          {renderInput('closingDate', 'Closing Date', orgInfo.closingDate, handleOrgChange, true, 'date')}
        </div>
        <div style={styles.rowWrap}>
          {renderSelect('location', 'Location', dropdownOptions.location, -1)}
          {renderSelect('organizationCategory', 'Organization Category', dropdownOptions.organizationCategory, -1)}
        </div>
        {renderTextarea('companyDetails', 'Company Details', orgInfo.companyDetails, handleOrgChange)}
        {renderTextarea('applicationMethod', 'Application Method', orgInfo.applicationMethod, handleOrgChange)}
      </div>

      <h3 style={{ marginBottom: '16px' }}>Job Positions</h3>
      {jobs.map((job, i) => (
        <div key={i} className="card" style={{ marginBottom: '16px', position: 'relative' }}>
          {jobs.length > 1 && (
            <button 
              onClick={() => removeJobField(i)} 
              className="btn-secondary" 
              style={{ position: 'absolute', top: '16px', right: '16px', padding: '4px 8px', fontSize: '12px', color: 'var(--error-color)', borderColor: 'var(--border-color)' }}
            >
              Remove
            </button>
          )}
          <h4 style={{ marginTop: 0, marginBottom: '16px', color: 'var(--brand-secondary)' }}>Position #{i + 1}</h4>
          
          <div style={styles.rowWrap}>
            {renderInput('title', 'Title', job.title, (e) => handleJobChange(i, e), true)}
            {renderSelect('jobType', 'Job Type', dropdownOptions.jobType, i)}
          </div>
          <div style={styles.rowWrap}>
            {renderSelect('educationLevel', 'Education Level', dropdownOptions.educationLevel, i)}
            {renderSelect('requiredField', 'Field Required', dropdownOptions.requiredField, i)}
          </div>
          {renderSelect('experienceLevel', 'Experience Level', dropdownOptions.experienceLevel, i)}
          <div style={styles.rowWrap}>
            {renderInput('salaryRange', 'Salary Range', job.salaryRange, (e) => handleJobChange(i, e))}
            {renderInput('jobUrl', 'Job URL', job.jobUrl, (e) => handleJobChange(i, e))}
          </div>
          <div style={styles.rowWrap}>
            {renderInput('imgUrl', 'Image URL', job.imgUrl, (e) => handleJobChange(i, e))}
            {renderInput('pdfUrl', 'PDF URL', job.pdfUrl, (e) => handleJobChange(i, e))}
          </div>
          {renderTextarea('requirements', 'Requirements', job.requirements, (e) => handleJobChange(i, e))}
        </div>
      ))}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px' }}>
        <button onClick={addJobField} className="btn-secondary">+ Add Another Job Position</button>
        <button onClick={handleSubmit} className="btn-primary" style={{ padding: '0.75rem 2rem' }}>🚀 Submit All Jobs</button>
      </div>

      {showPreview && (
        <div style={styles.overlay} onClick={(e) => { if (e.target === e.currentTarget) setShowPreview(false); }}>
          <div className="card" style={{ maxWidth: '800px', width: '90%', maxHeight: '90vh', overflowY: 'auto', position: 'relative' }}>
            <button onClick={() => setShowPreview(false)} className="btn-secondary" style={{ position: 'absolute', top: '16px', right: '16px', border: 'none' }}>✕</button>
            <h3 style={{ marginTop: 0 }}>Preview Submission</h3>
            <div style={{ marginBottom: '24px' }}>
              <p><strong>Company:</strong> {orgInfo.companyName}</p>
              <p><strong>Website:</strong> {orgInfo.companyWebsite}</p>
              <p><strong>Location:</strong> {orgInfo.location}</p>
              <p><strong>Category:</strong> {orgInfo.organizationCategory}</p>
              <p><strong>Posted:</strong> {orgInfo.postedDate}</p>
              <p><strong>Closing:</strong> {orgInfo.closingDate}</p>
            </div>
            <div style={{ marginBottom: '24px' }}>
              <strong>Application Method:</strong>
              <div style={{ padding: '12px', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)', marginTop: '8px' }}>
                <ReactMarkdown>{orgInfo.applicationMethod}</ReactMarkdown>
              </div>
            </div>
            <div style={{ marginBottom: '24px' }}>
              <strong>Company Details:</strong>
              <div style={{ padding: '12px', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)', marginTop: '8px' }}>
                <ReactMarkdown>{orgInfo.companyDetails}</ReactMarkdown>
              </div>
            </div>
            <h4 style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '8px' }}>Jobs:</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {jobs.map((job, i) => (
                <div key={i} style={{ padding: '16px', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)' }}>
                  <h5 style={{ margin: '0 0 8px 0', fontSize: '1rem' }}>{job.title}</h5>
                  <p style={{ margin: '0 0 4px 0', fontSize: '0.875rem' }}>{job.jobType} • {job.educationLevel} • {job.requiredField} • {job.experienceLevel}</p>
                  <p style={{ margin: '0 0 4px 0', fontSize: '0.875rem' }}><strong>Salary:</strong> {job.salaryRange || 'N/A'}</p>
                  {job.jobUrl && <p style={{ margin: '0 0 4px 0', fontSize: '0.875rem', wordBreak: 'break-all' }}><strong>URL:</strong> {job.jobUrl}</p>}
                  <div style={{ marginTop: '12px', fontSize: '0.875rem' }}>
                    <strong>Requirements:</strong>
                    <div style={{ marginTop: '4px' }}>
                      <ReactMarkdown>{job.requirements}</ReactMarkdown>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button onClick={() => { setShowPreview(false); handleSubmit(); }} className="btn-primary">Submit Now</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { maxWidth: '1000px', margin: '0 auto', padding: '32px 20px' },
  rowWrap: { display: 'flex', gap: '16px', flexWrap: 'wrap' },
  fieldGroup: { marginBottom: '16px', flex: '1 1 300px' },
  backButton: { marginBottom: '24px', padding: '8px 16px', display: 'inline-block' },
  overlay: {
    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
    backgroundColor: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999
  },
  dialog: {
    backgroundColor: 'var(--bg-primary)', padding: '32px', borderRadius: 'var(--radius-lg)',
    textAlign: 'center', boxShadow: 'var(--shadow-lg)'
  },
  spinner: {
    width: '40px', height: '40px', border: '3px solid var(--border-color)',
    borderTop: '3px solid var(--brand-primary)', borderRadius: '50%',
    animation: 'spin 1s linear infinite', margin: '20px auto 0'
  }
};

export default AddJob;
